import { sampleQueryKeys } from '@/config/queryKeys';
import { defaultColDef } from '@/constants';
import { useAgGridFitWhenStable, useDateFormatter, useRouteProtection } from '@/hooks';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { type PaginatedSamplesResponse, SUPER_ADMIN_ROLE, type SampleWithNamesPublic } from '@/types';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import type {
  ColDef,
  GridApi,
  ICellRendererParams,
  IRowNode,
  RowClassParams,
  RowClickedEvent,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import React, { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import globalStyles from '../../styles/index.module.css';

import InstrumentRunCellRenderer from './components/InstrumentRunCellRenderer';
import ProteoformDetailRenderer from './components/ProteoformDetailRenderer';
import SampleNameCellRenderer from './components/SampleNameCellRenderer';
import StudyCellRenderer from './components/StudyCellRenderer';
import { PAGE_SIZE } from './constants/PAGE_SIZE';
import { useOrgId } from './helpers';
import styles from './styles.module.css';

const handleRowClicked = (event: RowClickedEvent<SampleWithNamesPublic>) => {
  const mouseEvent = event.event;
  if (
    mouseEvent &&
    ((mouseEvent.target as HTMLElement)?.closest('[data-testid="study-link-button"]') ||
      (mouseEvent.target as HTMLElement)?.closest('[data-testid="instrument-run-link-button"]') ||
      (mouseEvent.target as HTMLElement)?.closest('[data-testid="sample-link-button"]'))
  ) {
    return;
  }
  const node = event.node;
  if (node) {
    node.setExpanded(!node.expanded);
  }
};

export const SamplesPage: FC = () => {
  // Protect this route - will redirect if user loses SuperAdmin role or switches org
  useRouteProtection([SUPER_ADMIN_ROLE]);
  const { t } = useTranslation(['studies', 'general']);
  const orgId = useOrgId();
  const gridRef = useRef<AgGridReact<SampleWithNamesPublic> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const formatDate = useDateFormatter();

  const colDefs = useMemo<ColDef<SampleWithNamesPublic>[]>(
    () => [
      {
        field: 'id',
        headerName: t('studies.columnHeaders.sampleNum', 'ID'),
        hide: true,
        sortable: true,
      },
      {
        field: 'name',
        headerName: t('studies.columnHeaders.sampleName', 'Name'),
        sortable: true,
        cellRenderer: SampleNameCellRenderer,
      },
      {
        field: 'study_name',
        headerName: t('studies.columnHeaders.study', 'Study'),
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => <StudyCellRenderer data={params.data} />,
      },
      {
        field: 'instrument_run_name',
        headerName: t('studies.columnHeaders.instrumentRun', 'Instrument Run'),
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <InstrumentRunCellRenderer data={params.data} />
        ),
      },
      {
        field: 'well_position',
        headerName: t('studies.columnHeaders.wellPosition', 'Well Position'),
      },
      {
        field: 'created_at',
        headerName: t('studies.columnHeaders.createdAt', 'Created At'),
        hide: true,
        valueFormatter: ({ value }: { value: string }) => formatDate(value),
      },
    ],
    [formatDate, t],
  );

  const datasource = useMemo(() => {
    return orgId
      ? createAgGridDatasource<SampleWithNamesPublic, PaginatedSamplesResponse>(
          sampleQueryKeys.endpoints.listSearch(orgId),
        )
      : undefined;
  }, [orgId]);

  const handleGridReady = (params: { api: GridApi<SampleWithNamesPublic> }) => {
    if (datasource) {
      params.api.setGridOption('serverSideDatasource', datasource);
    }
  };

  // All rows are potential masters — the API doesn't return a proteoform count.
  // The detail renderer handles the empty/error case by collapsing the row automatically.
  const isRowMaster = useCallback((_data: SampleWithNamesPublic) => true, []);

  const getRowClass = useCallback((params: RowClassParams<SampleWithNamesPublic>) => {
    if (params.node.detail) {
      return styles.detailRow;
    }
    return undefined;
  }, []);

  useAgGridFitWhenStable(gridRef, containerRef, [datasource]);

  const handleFirstDataRendered = useCallback(() => {
    if (gridRef.current?.api && containerRef.current) {
      gridRef.current.api.sizeColumnsToFit();
    }
  }, []);

  const handleGridSizeChanged = useCallback(() => {
    if (gridRef.current?.api && containerRef.current) {
      gridRef.current.api.sizeColumnsToFit();
    }
  }, []);

  return (
    <Stack className={styles.container} fullHeight fullWidth orientation="vertical" gap={4}>
      <Text as="h1" size="xl">
        {t('studies.title', 'Samples')}
      </Text>

      <Table
        container={{ className: globalStyles.scrollbars, ref: containerRef }}
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        detailCellRenderer={(props: {
          data: SampleWithNamesPublic;
          node: IRowNode<SampleWithNamesPublic>;
          api: GridApi<SampleWithNamesPublic>;
        }) => !!orgId && <ProteoformDetailRenderer data={props.data} orgId={orgId} node={props.node} api={props.api} />}
        domLayout="normal"
        enableResponsiveResize
        getRowId={(params: { data: SampleWithNamesPublic }) => params.data.id}
        getRowClass={getRowClass}
        isRowMaster={isRowMaster}
        masterDetail={true}
        onGridReady={handleGridReady}
        onRowClicked={handleRowClicked}
        onFirstDataRendered={handleFirstDataRendered}
        onGridSizeChanged={handleGridSizeChanged}
        pagination
        paginationPageSize={PAGE_SIZE}
        ref={gridRef}
        resizeDebounceMs={150}
        rowBuffer={30}
        rowModelType="serverSide"
        tableId={'samples-table'}
        zebraStriping
      />
    </Stack>
  );
};

export default SamplesPage;
